import React, { useState, ReactFragment, useEffect } from 'react';
import { Divider, Grid, Tooltip } from '@material-ui/core';
import TokenBannerImage from 'assets/images/token-banner.svg';
import { TeamUsers } from 'redux/models/teamUsers';
import CrownImage from 'assets/images/crown.svg';
import './LeaderboardPanel.scss';
import { Loader } from 'components/Loader';
import { Link } from 'react-router-dom';
import Confetti from 'react-confetti';
import debounce from 'lodash.debounce';
import EASTER_EGG_IDS from 'helpers/easterEggConstants';
import { useEasterEggFound } from 'helpers/easterEgghelper';

interface LeaderboardPanelProps {
	loading: boolean;
	userId: string;
	teamId: string;
	teamUsers: TeamUsers[];
}

const SMALL_LEADERBOARD = 3;
const LARGE_LEADERBOARD = 5;

const LeaderboardPanel: React.FC<LeaderboardPanelProps> = (props: LeaderboardPanelProps) => {
	const [dropConfetti, setDropConfetti] = useState(false);
	const [confettiHeight, setConfettiHeight] = useState(0);
	const [confettiWidth, setConfettiWidth] = useState(0);
	const [leaderboardTitleClicked, setLeaderboardTitleClicked] = useState(0);
	const [topLeaderboardMembers, setTopLeaderboardMembers] = useState<TeamUsers[]>([]);
	const easterEggFound = useEasterEggFound(EASTER_EGG_IDS.CONFETTI_PARTY);
	const { loading, teamUsers, teamId, userId } = props;
	let view;
	let tooltipText = '🥚';

	const buildLeaderboardMap = (teamUsers: TeamUsers[]) => {
		let leaderboardMembers: ReactFragment[] = [];
		let place = 1;
		const leaderboardLength = teamUsers.length > 10 ? LARGE_LEADERBOARD : SMALL_LEADERBOARD;
		for (let index = 0; index < leaderboardLength; ++index) {
			if (index > teamUsers.length - 1) {
				break;
			}

			if (teamUsers[index - 1] && teamUsers[index - 1].tokens > teamUsers[index].tokens) {
				place += 1;
			}

			const member = teamUsers[index];

			leaderboardMembers.push(
				<React.Fragment key={index}>
					<Grid container alignItems='center' className={`leaderboard-member-wrapper ${member.userId === userId ? 'current-user' : ''}`}>
						<Grid container direction='row' alignItems='center' justifyContent='center' alignContent='center' spacing={0}>
							<Grid container item xs={2} className='place-text-grid-item' justifyContent='center'>
								{place === 1 ? <img src={CrownImage} alt='crown-icon' /> : place}
							</Grid>
							<Grid item xs={9} className='name-text-grid-item'>
								{member.userName}
							</Grid>
							<Grid container item xs={1} justifyContent='flex-end' className='token-count-text-grid-item'>
								{member.tokens}
							</Grid>
						</Grid>
					</Grid>
					{index !== teamUsers.length - 1 && <Divider className='leaderboard-divider' />}
				</React.Fragment>
			);
		}

		return leaderboardMembers;
	};

	if (loading) {
		view = <Loader />;
	} else if (teamUsers.length > 0 && !loading) {
		view = buildLeaderboardMap(teamUsers);
	} else if (teamUsers.length === 0 && !loading) {
		view = <div className='leaderboard-no-members'>No members at this time!</div>;
	}

	if (leaderboardTitleClicked === 0) {
		tooltipText = '🥚';
	} else if (leaderboardTitleClicked === 1) {
		tooltipText = 'Getting closer';
	} else if (leaderboardTitleClicked === 2) {
		tooltipText = 'Almost there';
	}

	const onLeaderboardTitleClicked = () => {
		if (leaderboardTitleClicked < 2) {
			setLeaderboardTitleClicked(leaderboardTitleClicked + 1);
		} else {
			setDropConfetti(true);
			setLeaderboardTitleClicked(0);
			easterEggFound();
		}
	};

	useEffect(() => {
		const leaderboardLength = teamUsers.length > 10 ? LARGE_LEADERBOARD : SMALL_LEADERBOARD;
		const members = teamUsers.filter((member, index) => index < leaderboardLength);
		setTopLeaderboardMembers(members);
	}, [teamUsers, userId]);

	useEffect(() => {
		const debouncedHandleResize = debounce(() => {
			const leaderboardParent = document.getElementById('leaderboard-id')?.parentElement;
			setConfettiHeight(leaderboardParent ? leaderboardParent.clientHeight : 0);
			setConfettiWidth(leaderboardParent ? leaderboardParent.clientWidth : 0);
		}, 300);

		const leaderboardParent = document.getElementById('leaderboard-id')?.parentElement;
		setConfettiHeight(leaderboardParent ? leaderboardParent.clientHeight : 0);
		setConfettiWidth(leaderboardParent ? leaderboardParent.clientWidth : 0);

		window.addEventListener('resize', debouncedHandleResize);

		return () => window.removeEventListener('resize', debouncedHandleResize);
	}, []);

	return (
		<Grid container id='leaderboard-id' direction='column' alignItems='center' className='leaderboard-panel'>
			<Confetti
				height={confettiHeight}
				width={confettiWidth}
				gravity={0.2}
				numberOfPieces={dropConfetti ? 1000 : 0}
				recycle={false}
				onConfettiComplete={confetti => {
					setDropConfetti(false);
					confetti?.reset();
				}}
			/>
			<Grid item>
				<Tooltip title={tooltipText}>
					<img src={TokenBannerImage} className='banner-image' alt='banner' onClick={onLeaderboardTitleClicked} />
				</Tooltip>
			</Grid>
			<Grid item className='leaderboard-title-wrapper'>
				<div className='leaderboard-title'>{`Top ${teamUsers.length > 10 ? LARGE_LEADERBOARD : SMALL_LEADERBOARD} Leaderboard`}</div>
			</Grid>

			<div className='leaderboardListWrapper'>{view}</div>

			{teamUsers.length > 3 && (
				<Link to={`/team/${teamId}/details/leaderboard`} className='leaderboard-all-members-text'>
					{`+ ${teamUsers.length - topLeaderboardMembers.length} more team members `}
				</Link>
			)}
		</Grid>
	);
};

export default LeaderboardPanel;
